import React, { FC, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import isEqual from 'lodash/isEqual';

import { RadioSelectOption } from '../../../../../../components/inputs/RadioSelectScrollable';
// components
import BasicModal from '../../../../../../components/modals/basicModal';
import InlineNotification from '../../../../../../components/snackBar/InlineNotification';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../../../constants/aampeTheme';
import { ToastStatus } from '../../../../../../constants/toastMessage.constants';
import { ModalHeaderStyle } from '../../../../../attributes/constants';

// context
import { EditorContext } from '../../../../../../context/EditorContext';

import useDatasetsQuery from 'hooks/datasets/useDatasetByIdQuery';
// hooks
import useDatasetFiltersMutation from 'hooks/formulas/useDatasetFilters';
import useGetCurrentMessageContent from 'hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from 'hooks/message-content/useMessageContentGetAndUpdate';

// models
import { DatasetFiltersType } from 'models/dataset-filters.model';

import { ActionButtonText } from '../contants';
import { getDatasetFieldIds } from '../helper';
import DatasetConfigurationalFooter from './DatasetConfigurationalFooter';
import DatasetConfigurationalHeader from './DatasetConfigurationalHeader';
import DatasetContentTable from './DatasetContentTable';
import DatasetSelectorV1 from './DatasetSelectorV1';

interface DatasetConfigurationalModalV1Props {
    handleClose: () => void;
    isEditable?: boolean;
    showColumnSelect?: boolean;
    handleColumnSelect?: (id: string) => void;
    selectedFieldId?: string;
    setSelectedFieldId?: (id: string) => void;
}

const modalHeaderStyles = {
    ...ModalHeaderStyle,
    backgroundColor: aampeTheme.colors.backgroundWG02,
};
const DatasetConfigurationalModalV1: FC<DatasetConfigurationalModalV1Props> = ({
    handleClose,
    isEditable = true,
    showColumnSelect = false,
    handleColumnSelect,
    selectedFieldId,
    setSelectedFieldId,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const { formulaId } = useParams<{ formulaId: string }>();

    const [selectedDatasetId, setSelectedDatasetId] = useState<
        number | undefined
    >(formula?.dataset_id);

    const { data: filteredDataset, isLoading: datasetLoading } =
        useDatasetsQuery(selectedDatasetId);

    const [isContentTableComponent, setIsContentTableComponent] =
        useState<boolean>(!!formula?.dataset_id);

    const [rowCount, setRowCount] = useState<number>(0);
    const [datasetFilters, setDatasetFilters] = useState<DatasetFiltersType>(
        formula?.dataset_filters?.filters || {},
    );

    const actionButtonText = useMemo<string>(() => {
        return isContentTableComponent
            ? ActionButtonText.ADD
            : ActionButtonText.NEXT;
    }, [isContentTableComponent]);

    const filtersChanged = useMemo(() => {
        if (!formula?.dataset_id) return true;
        return !isEqual(datasetFilters, formula?.dataset_filters?.filters);
    }, [
        datasetFilters,
        formula?.dataset_id,
        formula?.dataset_filters?.filters,
    ]);

    const isActionDisabled = useMemo(() => {
        if (actionButtonText === ActionButtonText.NEXT) {
            return !selectedDatasetId;
        } else if (actionButtonText === ActionButtonText.ADD) {
            return (
                !filtersChanged &&
                (selectedFieldId === null || selectedFieldId === undefined) &&
                selectedDatasetId === formula?.dataset_id
            );
        }
        return false;
    }, [
        actionButtonText,
        selectedDatasetId,
        filtersChanged,
        selectedFieldId,
        formula?.dataset_id,
    ]);

    const columnOptions = useMemo<RadioSelectOption[]>(() => {
        if (filteredDataset?.fields) {
            const options: RadioSelectOption[] = filteredDataset.fields.map(
                (field) => ({
                    label: field.name,
                    value: String(field.id),
                }),
            );
            return options;
        }
        return [];
    }, [filteredDataset?.fields]);

    const { mutate } = useDatasetFiltersMutation(Number(formulaId));

    const messageContentUpdate = useMessageContentGetAndUpdate();
    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );

    const handleSetDatasetFilters = (filters: DatasetFiltersType) => {
        if (selectedDatasetId === formula?.dataset_id) {
            mutate(
                {
                    formulaId,
                    data: {
                        filters: filters,
                        message_content_id: currentMessageContentId,
                    },
                },
                {
                    onSuccess: () => handleClose(),
                },
            );
        } else {
            messageContentUpdate.mutate(
                {
                    id: currentMessageContentId!,
                    dataset_id: selectedDatasetId || formula?.dataset_id!,
                },
                {
                    onSuccess: () => {
                        mutate({
                            formulaId,
                            data: {
                                filters: filters,
                                message_content_id: currentMessageContentId,
                            },
                        });
                        handleClose();
                    },
                },
            );
        }
    };

    const appliedDatasetFieldIds = useMemo<number[]>(() => {
        if (formula) {
            return getDatasetFieldIds(formula);
        }
        return [];
    }, [formula]);

    const handleButtonAction = () => {
        if (actionButtonText === ActionButtonText.NEXT) {
            setIsContentTableComponent(true);
        } else {
            if (selectedFieldId !== null && selectedFieldId !== undefined) {
                handleColumnSelect?.(selectedFieldId);
                handleClose();
            }
            if (
                !isEqual(datasetFilters, formula?.dataset_filters?.filters) ||
                selectedDatasetId !== formula?.dataset_id
            ) {
                handleSetDatasetFilters(datasetFilters);
            }
        }
    };

    return (
        <BasicModal
            childContainerStyle={{
                padding: aampeTheme.spacing4(8),
            }}
            width={'min(1080px, 95vw)'}
            handleClose={handleClose}
            headerStyle={modalHeaderStyles}
            containerBodyStyle={{
                backgroundColor: aampeTheme.colors.backgroundWG02,
                overflowY: 'auto',
            }}
        >
            <Container>
                <ContainerTop>
                    <DatasetConfigurationalHeader
                        isEditable={isEditable}
                        isContentTableComponent={isContentTableComponent}
                        selectedDatasetId={selectedDatasetId}
                        appliedDatasetFieldIds={appliedDatasetFieldIds}
                        setSelectedDatasetId={setSelectedDatasetId}
                        showHeadingDescription={
                            isContentTableComponent &&
                            selectedFieldId === undefined &&
                            showColumnSelect
                        }
                    />
                    <ContentWrapper>
                        {isContentTableComponent && (
                            <WarningWrapper
                                status={ToastStatus.Info}
                                title={'About Dataset Filters'}
                                description={
                                    'Since each message uses just one dataset, any filter change will impact all instances.'
                                }
                                displayIcon
                                closeButton
                            />
                        )}
                        {isContentTableComponent &&
                        filteredDataset &&
                        selectedDatasetId ? (
                            <DatasetContentTable
                                isEditable={isEditable}
                                datasetId={selectedDatasetId}
                                setRowCount={setRowCount}
                                datasetFilters={datasetFilters}
                                setDatasetFilters={setDatasetFilters}
                                filteredDataset={filteredDataset}
                                datasetLoading={datasetLoading}
                            />
                        ) : (
                            <DatasetSelectorV1
                                isEditable={isEditable}
                                setSelectedDatasetId={setSelectedDatasetId}
                            />
                        )}
                    </ContentWrapper>
                </ContainerTop>
                <DatasetConfigurationalFooter
                    isEditable={isEditable}
                    rowCount={rowCount}
                    showRowCount={isContentTableComponent}
                    actionButtonText={actionButtonText}
                    handleClose={handleClose}
                    handleChange={handleButtonAction}
                    isActionDisabled={isActionDisabled}
                    showColumnSelect={showColumnSelect}
                    setSelectedFieldId={setSelectedFieldId}
                    selectedFieldId={selectedFieldId}
                    columnOptions={columnOptions}
                />
            </Container>
        </BasicModal>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    height: 'min(690px, 85vh)',
    gap: aampeTheme.spacing4(6),
    width: '100%',
    maxWidth: '1016px',
});

const ContentWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing4(6),
    borderRadius: aampeTheme.borderRadius.medium,
    width: '100%',
    height: 'min(530px, 65vh)',
    overflowY: 'auto',
});

const WarningWrapper = styled(InlineNotification)({
    height: 'auto',
});

const ContainerTop = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

export default DatasetConfigurationalModalV1;
